import { writable, derived, type Readable } from 'svelte/store';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from './supabase';
import type { LysFamilie, Plante, Potte, PottePlanteFull } from './database.types';
import { medPlantebilde } from './plantebilde';

/** Auth session — null when logged out. */
export const session = writable<Session | null>(null);

/** Logged-in user — null when logged out. */
export const user: Readable<User | null> = derived(session, ($s) => $s?.user ?? null);

// INITIAL_SESSION kommer fra samme strøm som senere auth-hendelser. En separat
// getSession()-promise kunne fullføre etter SIGNED_IN og overskrive den nye økten.
supabase.auth.onAuthStateChange((_event, newSession) => session.set(newSession));

// ============= REFERENCE DATA (cached after first load) =============

export const lysFamilier = writable<LysFamilie[]>([]);
export const planter = writable<Plante[]>([]);
export const potter = writable<Potte[]>([]);

/** Indexed by potte_id → list of plants in that pot. Refreshed by loadPottePlanter(). */
export const pottePlanter = writable<Record<string, PottePlanteFull[]>>({});

function medBildeIPottePlante(p: PottePlanteFull): PottePlanteFull {
  return { ...p, plante: p.plante ? medPlantebilde(p.plante) : p.plante };
}

let referenceLoaded = false;
let referansePagaar: Promise<void> | null = null;
let dataGenerasjon = 0;
let nestePlanteHenting = 0;
let sisteAlleHenting = 0;
let sisteAllePublisert = 0;
const sistePotteHenting = new Map<string, number>();

/**
 * Hent katalog, lys-familier og kasser (én gang per innlogging).
 *
 * Kaster hvis noe av det feiler (nettverk, Supabase på pause, RLS-avslag).
 * Før ble feilen svelget og hentingen merket som ferdig — da viste appen
 * «Ingen blomsterkasser registrert ennå» og kom seg aldri videre uten omstart.
 * Nå kan App vise en feilmelding med «Prøv igjen». Samtidige kall deler én
 * henting (auth-hendelser kan trigge flere på rad ved oppstart).
 */
export function loadReferenceData(): Promise<void> {
  if (referenceLoaded) return Promise.resolve();
  if (referansePagaar) return referansePagaar;

  const generasjon = dataGenerasjon;
  const henting = (async () => {
    const [familier, planteListe, potteListe] = await Promise.all([
      supabase.from('lys_familier').select('*').order('sortering'),
      supabase.from('planter').select('*').order('navn'),
      supabase.from('potter').select('*').order('potte_id'),
    ]);

    const feil = familier.error ?? planteListe.error ?? potteListe.error;
    if (feil) {
      console.error('loadReferenceData:', feil);
      throw new Error(feil.message || 'Kunne ikke hente data');
    }

    if (generasjon === dataGenerasjon) {
      lysFamilier.set(familier.data ?? []);
      planter.set((planteListe.data ?? []).map(medPlantebilde));
      potter.set(potteListe.data ?? []);
      referenceLoaded = true;
    }
  })();
  const delt = henting.finally(() => {
    if (referansePagaar === delt) referansePagaar = null;
  });
  referansePagaar = delt;
  return delt;
}

/**
 * Glem dataene ved utlogging. Neste innlogging henter på nytt, og svar fra
 * pågående spørringer får ikke fylle storene etter at økten er avsluttet.
 */
export function glemReferanseData(): void {
  dataGenerasjon++;
  referenceLoaded = false;
  referansePagaar = null;
  lysFamilier.set([]);
  planter.set([]);
  potter.set([]);
  pottePlanter.set({});
  sistePotteHenting.clear();
}

export async function loadPottePlanter(potteId: string): Promise<void> {
  const generasjon = dataGenerasjon;
  const henting = ++nestePlanteHenting;
  sistePotteHenting.set(potteId, henting);
  const { data, error } = await supabase
    .from('potte_planter')
    .select('*, plante:planter(*)')
    .eq('potte_id', potteId)
    .is('fjernet_at', null)
    .order('seksjon');

  if (error) {
    console.error('loadPottePlanter:', error);
    return;
  }

  if (generasjon !== dataGenerasjon || sistePotteHenting.get(potteId) !== henting || sisteAllePublisert > henting) return;
  pottePlanter.update((m) => ({ ...m, [potteId]: ((data ?? []) as unknown as PottePlanteFull[]).map(medBildeIPottePlante) }));
}

export async function loadAllPottePlanter(): Promise<void> {
  const generasjon = dataGenerasjon;
  const henting = ++nestePlanteHenting;
  sisteAlleHenting = henting;
  const { data, error } = await supabase
    .from('potte_planter')
    .select('*, plante:planter(*)')
    .is('fjernet_at', null)
    .order('seksjon');

  if (error) {
    console.error('loadAllPottePlanter:', error);
    return;
  }

  if (generasjon !== dataGenerasjon || sisteAlleHenting !== henting) return;
  sisteAllePublisert = henting;
  const grouped: Record<string, PottePlanteFull[]> = {};
  for (const row of ((data ?? []) as unknown as PottePlanteFull[]).map(medBildeIPottePlante)) {
    if (!grouped[row.potte_id]) grouped[row.potte_id] = [];
    grouped[row.potte_id]!.push(row);
  }
  pottePlanter.update((forrige) => {
    const neste = { ...forrige };
    for (const id of new Set([...Object.keys(forrige), ...Object.keys(grouped)])) {
      if ((sistePotteHenting.get(id) ?? 0) > henting) continue;
      if (grouped[id]) neste[id] = grouped[id];
      else delete neste[id];
    }
    return neste;
  });
}
